// react
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {useState} from 'react';
// material
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Button,
	Card,
	Container,
	Divider,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import {useNavigate} from 'react-router-dom';
import {toast} from 'react-toastify';
import AccessControlWrapper from 'src/components/AccessControlWrapper';
import CustomFields from 'src/components/inputs/CustomFields';
import FormikWrapper from 'src/components/wrappers/FormikWrapper';
import ImageCropPicker from 'src/components/inputs/ImageCropPicker.js/ImageCropPicker';
import Picker from 'src/components/inputs/Picker';
import PublishSwitch from 'src/pages/products/common/PublishSwitch';
import SubmitButton from 'src/components/buttons/SubmitButton';
import AddPropertyValueModal from 'src/components/_dashboard/properties/AddPropertyValueModal';
import SelectPropertyModal from 'src/components/_dashboard/properties/SelectPropertyModal';
import {ScrollToFieldError} from 'src/hooks/ScrollToFieldError';
import useCategories from 'src/hooks/useCategories';
import {addProductPermission} from 'src/permissionsList';
import {isNullOrEmpty} from 'src/utils/helperMethods';
// form handling
import Dropzone from '../../../components/inputs/dropzone';
// components
import Page from '../../../components/Page';
import {addProduct} from '../../../utils/AdminApi';
import PropertiesSelectionList from './PropertiesSelectionList';
import {useProductPropertyToVariationHook} from './useProductPropertyToVariationHook';
import VariationsForm from './VariationsForm';
import AvailabilityRadioButtons from '../common/AvailabilityRadioButtons';
import {addProductSchema} from '../product.schema';
import {getNumber} from 'src/utils/numberUtils';
import DiscountPicker from '../common/DiscountPicker';
import BranchPicker from 'src/components/BranchPicker';
import useUser from 'src/hooks/useUser';
import TutorialTip from 'src/components/TutorialTip';
import AddCategoryModal from 'src/components/modals/AddCategoryModal';
import GalleryPicker from 'src/components/GalleryPicker';
import {isFeatureAllowed} from 'src/utils/featureUtils';

// ----------------------------------------------------------------------
export default function EcommerceShop() {
	const navigate = useNavigate();
	const {categories} = useCategories();
	const [categoryModalOpen, setCategoryModalOpen] = useState(false);
	const {
		variations,
		setVariations,
		allSiteProperties,
		selectedProperties,
		setProperties,
		setSelectedProperties,
		pvModalIsVisible,
		setPvModalVisibility,
		propertyToAddTo,
		setPropertyToAddTo,
		onNewPropertyValueAdded,
	} = useProductPropertyToVariationHook();

	const {user} = useUser();
	const branch = user.branch;
	const isGalleryFeatureAllowed = false; // isFeatureAllowed('gallery_product_images', true);

	const onSubmit = async (v) => {
		const {mainImage, images, ...product} = v;

		// Filter out the main image from the images array to avoid duplication
		const otherImages = mainImage ? images.filter((img) => img.id !== mainImage.id) : images;

		// Extract image IDs (images are already uploaded, we just need to send their IDs)
		const mainImageId = mainImage?.id || otherImages[0]?.id || null;
		const imageIds = otherImages.map((img) => img.id).filter((id) => id !== mainImageId); // Avoid duplication

		const {id: productId} = await toast.promise(
			addProduct(
				{
					...product,
					price: getNumber(product.price, 0),
					main_image: isGalleryFeatureAllowed ? mainImageId : undefined,
					images: isGalleryFeatureAllowed ? imageIds : undefined,
					variations: variations.map((v) => ({
						...v,
						price: !isNullOrEmpty(v.price) ? v.price : null /* we want to store as null not 0 or "" */,

						discount: !isNullOrEmpty(v.discount)
							? v.discount
							: null /* we want to store as null not 0 or "" */,
					})),
				},
				isGalleryFeatureAllowed ? null : mainImage, // No file to upload for main_image
				isGalleryFeatureAllowed ? null : images, // No files to upload for images
			),
			{
				error: 'حدث خطا غير متوقع',
				success: 'تم انشاء المنتج بنجاح',
				pending: 'يرجي الانتظار',
			},
		);

		navigate('/products/' + productId);
	};

	return (
		<Page title="اضافة منتج">
			<AccessControlWrapper controllerAction={addProductPermission}>
				<Container maxWidth="md">
					<FormikWrapper
						hideSubmitButton
						values={initialValues}
						schema={addProductSchema}
						handleSubmit={onSubmit}
					>
						{({errors, touched, isSubmitting, values, getInputProps, setFieldTouched, setFieldValue}) => (
							<Stack sx={{mb: 2}} spacing={2}>
								<Stack spacing={1} direction="row" justifyContent="space-between">
									<Typography variant="h4" sx={{mb: 5}}>
										اضافة منتج
									</Typography>
									<PublishSwitch
										value={values.publish_at != null}
										onChange={(newValue) => setFieldValue('publish_at', newValue)}
									/>
								</Stack>
								<Stack spacing={3}>
									{/* Main info Card */}
									<Card sx={{p: 4, flex: 1}}>
										<Stack spacing={3}>
											<ScrollToFieldError />
											<Stack spacing={1} justifyContent="space-between">
												{!isGalleryFeatureAllowed && (
													<ImageCropPicker
														maxSize={3}
														placeholder={'الصورة الرئيسية'}
														{...getInputProps('mainImage')}
														setFile={(file) => setFieldValue('mainImage', file)}
													/>
												)}

												<Stack flex={1} justifyContent={'space-between'} spacing={2}>
													<TextField fullWidth type="text" label="اسم المنتج" {...getInputProps('name')} />

													{isGalleryFeatureAllowed && (
														<GalleryPicker
															value={values.images}
															onChange={(newValue) => {
																setFieldValue('images', newValue);
																// If main image is not in the new selection, clear it
																if (values.mainImage && !newValue.find((img) => img.id === values.mainImage.id)) {
																	setFieldValue('mainImage', null);
																}
															}}
															forceCrop
															showDeleteButton
															allowMultiple
															maxFiles={10}
															enableCrop
															label="صور المنتج"
															placeholder="صور المنتج"
															enableMainImage
															mainImage={values.mainImage}
															onMainImageChange={(file) => {
																setFieldValue('mainImage', file);
															}}
														/>
													)}

													<TextField
														fullWidth
														type="text"
														minRows={3}
														multiline
														label="تفاصيل المنتج"
														{...getInputProps('details')}
													/>
												</Stack>
											</Stack>

											<Stack direction="row" spacing={1}>
												<Picker
													multiple
													{...getInputProps('categories')}
													label="التصنيف"
													options={categories}
													labelKey="name"
													valueKey="id"
													value={values.categories}
													onChange={(e) => setFieldValue('categories', e.target.value)}
													sx={{flex: 1}}
												/>
												<Button size="small" onClick={() => setCategoryModalOpen(true)}>
													إضافة
												</Button>
											</Stack>
											<AvailabilityRadioButtons
												value={values.infinite}
												onChange={(newValue) => setFieldValue('infinite', newValue)}
											/>

											<Stack spacing={1} direction="row" alignItems="center" justifyContent="space-between">
												<Stack alignItems={'start'}>
													<Typography>هل هذا المنتج يحتوي علي اختيارات مثل المقاس واللون ؟</Typography>
													<TutorialTip
														content={{
															type: 'video',
															url: 'https://www.youtube.com/embed/MNv3oafBN1k',
															title: 'كيفية تحديد خيارات المنتج',
															description: 'شرح لكيفية اضافة خيارات للمنتج مثل اللون والمقاس',
														}}
														triggerSx={{fontSize: 12}}
														triggerType="text"
														triggerText="شرح توضيحي"
														modalSize="large"
													/>
												</Stack>
												<SelectPropertyModal
													properties={allSiteProperties}
													setProperties={setProperties}
													onPropertiesSelected={(newProperties) =>
														setSelectedProperties([...selectedProperties, ...newProperties])
													}
													propertiesNotSelected={allSiteProperties.filter(
														(s) => !selectedProperties.find((sp) => sp.id === s.id),
													)}
												/>
											</Stack>

											<Divider />

											{selectedProperties.length === 0 && !values.infinite && (
												<Stack direction={{md: 'row'}} spacing={{xs: 1, md: 2}}>
													<TextField
														id="addedQuantity"
														name="addedQuantity"
														label="الكمية"
														autoComplete="off"
														sx={{flex: '1 1 auto'}}
														type="number"
														{...getInputProps('addedQuantity')}
													/>
													<TextField
														id="cost_per_unit"
														name="cost_per_unit"
														label="تكلفة القطعة"
														autoComplete="off"
														sx={{flex: '1 1 auto'}}
														type="number"
														{...getInputProps('cost_per_unit')}
													/>
													{!branch && (
														<BranchPicker
															value={values.branch ?? ''}
															onChange={(e) => setFieldValue('branch', e.target.value)}
															variant="outlined"
															allowNone={false}
														/>
													)}
												</Stack>
											)}

											<Stack spacing={2} direction="row" justifyContent="space-between">
												<TextField
													fullWidth
													type="text"
													inputMode="numeric"
													pattern="\d*"
													InputProps={{min: 0}}
													label="السعر"
													{...getInputProps('price')}
												/>
												<DiscountPicker
													sx={{maxWidth: 90}}
													name="discount"
													label="التخفيض"
													value={values.discount ?? 0}
													onChange={(discountValue) => {
														setFieldValue('discount', discountValue);
													}}
												/>
											</Stack>

											{selectedProperties.length > 0 && (
												<Typography variant="caption">
													سيتم استخدام هذا السعر في حالة عدم تحديد السعر لكل خيار
												</Typography>
											)}
										</Stack>
									</Card>

									{selectedProperties.length > 0 && (
										<Card sx={{p: 4, flex: 1}}>
											<PropertiesSelectionList
												allSiteProperties={allSiteProperties}
												selectedProperties={selectedProperties}
												setSelectedProperties={setSelectedProperties}
												setPvModalVisibility={setPvModalVisibility}
												setPropertyToAddTo={setPropertyToAddTo}
											/>

											{!isNullOrEmpty(variations) && (
												<VariationsForm variations={variations} setVariations={setVariations} values={values} />
											)}
										</Card>
									)}
									{/* Additional info card */}
									<Card sx={{p: 4, flex: 1}}>
										<Accordion>
											<AccordionSummary expandIcon={<ExpandMoreIcon />}>
												<Typography>معلومات اضافية</Typography>
											</AccordionSummary>

											<AccordionDetails>
												<Stack spacing={2}>
													{!isGalleryFeatureAllowed && (
														<>
															<Typography>صور اضافية للمنتج</Typography>
															<Dropzone
																maxSize={1}
																allowMultiple
																selectedFiles={values.images}
																onFilesChanged={(img) => {
																	setFieldValue('images', img);
																}}
																{...getInputProps('images')}
															/>
														</>
													)}

													<TextField fullWidth type="text" label="كود المنتج" {...getInputProps('sku')} />

													<Stack spacing={1}>
														<Typography variant="body2">الوزن والأبعاد (اختياري)</Typography>
														<TextField
															fullWidth
															type="text"
															inputMode="numeric"
															pattern="\d*"
															InputProps={{min: 0}}
															label="الوزن"
															placeholder="الوزن بالكيلو"
															helpText="الوزن بالكيلو"
															{...getInputProps('weight')}
														/>
														<Typography color="grey" variant="caption">
															ادخل الوزن بالكيلو
														</Typography>

														<Stack spacing={1} direction="row" justifyContent="space-between">
															<TextField
																fullWidth
																InputProps={{min: 0}}
																pattern="\d*"
																type="text"
																inputMode="numeric"
																label="الطول"
																placeholder="الطول"
																helpText="الطول بالسنتيمتر"
																{...getInputProps('width')}
															/>
															<TextField
																fullWidth
																InputProps={{min: 0}}
																pattern="\d*"
																type="text"
																inputMode="numeric"
																label="العرض"
																placeholder="العرض"
																helpText="العرض بالسنتيمتر"
																{...getInputProps('height')}
															/>
															<TextField
																fullWidth
																InputProps={{min: 0}}
																pattern="\d*"
																type="text"
																inputMode="numeric"
																label="الارتفاع"
																placeholder="الارتفاع"
																helpText="الارتفاع بالسنتيمتر"
																{...getInputProps('_length')}
															/>
														</Stack>
														<Typography color="grey" variant="caption">
															ادخل الابعاد بالسنتيمتر
														</Typography>
													</Stack>

													<CustomFields
														title="جدول المواصفات "
														errors={errors.attrs}
														touches={touched.attrs}
														value={values.attrs}
														onChange={(newValue) => setFieldValue('attrs', newValue)}
														setFieldTouched={setFieldTouched}
													/>
												</Stack>
											</AccordionDetails>
										</Accordion>
									</Card>
								</Stack>
								<SubmitButton isSubmitting={isSubmitting}>اضافة</SubmitButton>

								<AddCategoryModal
									open={categoryModalOpen}
									onClose={() => setCategoryModalOpen(false)}
									onCategoryCreated={(newCategory) => {
										// Add the new category to the currently selected categories
										const currentCategories = values.categories || [];
										setFieldValue('categories', [...currentCategories, newCategory.id]);
									}}
								/>
							</Stack>
						)}
					</FormikWrapper>

					{/* <ProductCartWidget /> */}
				</Container>
				<AddPropertyValueModal
					isVisible={pvModalIsVisible}
					setVisibility={setPvModalVisibility}
					propertyId={propertyToAddTo}
					productProperties={allSiteProperties}
					onCreate={onNewPropertyValueAdded}
				/>
			</AccessControlWrapper>
		</Page>
	);
}

const initialValues = {
	sku: '',
	name: '',
	details: '',
	price: '',
	quantity: 0,
	categories: [],
	infinite: false,
	images: [],
	attrs: [],
	mainImage: null,
	publish_at: new Date(),
};
